import { Injectable } from '@angular/core';
import { ParentInfo } from '../models/student.model';

interface ProgressCriteria {
  singleParenting: boolean;
  weeklyWorkHours: number;
  hasSibling: boolean;
}

interface ProgressDetails {
  percentage: number;
  criteria: ProgressCriteria;
}

@Injectable({
  providedIn: 'root',
})
export class RegistrationProgressService {
  private readonly SINGLE_PARENT_SCORE = 40;
  private readonly SIBLING_SCORE = 20;
  private readonly MAX_WORK_HOURS_SCORE = 40;

  getProgressDetails(parentInfo: ParentInfo | unknown): ProgressDetails {
    if (!parentInfo) {
      return {
        percentage: 0,
        criteria: {
          singleParenting: false,
          weeklyWorkHours: 0,
          hasSibling: false,
        },
      };
    }
    const singleParenting = this.checkSingleParenting(parentInfo as ParentInfo);
    const weeklyWorkHours = this.calculateWorkHours(parentInfo as ParentInfo);
    const hasSibling = this.checkSibling(parentInfo as ParentInfo);

    const percentage = this.calculateTotalScore(
      singleParenting,
      weeklyWorkHours,
      hasSibling
    );

    return {
      percentage,
      criteria: {
        singleParenting,
        weeklyWorkHours,
        hasSibling,
      },
    };
  }

  private checkSingleParenting(parentInfo: ParentInfo): boolean {
    return parentInfo.singleParenting ?? false;
  }

  private checkSibling(parentInfo: ParentInfo): boolean {
    return parentInfo.hasSibling ?? false;
  }

  private calculateWorkHours(parentInfo: ParentInfo): number {
    const parent1Hours = parentInfo.parent1?.weeklyWorkHours ?? 0;

    if (parentInfo.singleParenting || !parentInfo.parent2) {
      return parent1Hours;
    }

    const parent2Hours = parentInfo.parent2?.weeklyWorkHours ?? 0;
    return (parent1Hours + parent2Hours) / 2;
  }

  private calculateTotalScore(
    singleParenting: boolean,
    weeklyWorkHours: number,
    hasSibling: boolean
  ): number {
    let score = 0;

    // Single parent score
    if (singleParenting) {
      score += this.SINGLE_PARENT_SCORE;
    }

    // Work hours score (proportional up to 40%)
    const workScore =
      (Math.min(weeklyWorkHours, 40) / 40) * this.MAX_WORK_HOURS_SCORE;
    score += workScore;

    // Sibling score
    if (hasSibling) {
      score += this.SIBLING_SCORE;
    }

    return Math.round(score);
  }
}
